import { useCallback, useMemo, useRef, useState } from "react";

import type { HistoryPoint, ItemObservation } from "../api/types";
import { adena, day, plural, signed } from "../format";
import { spreadOf } from "../market";
import { band, dayIndex, linear, niceTicks, path, segments, separate } from "./scale";
import "./chart.css";

// Geometry in viewBox units, shared by every chart in the stack. They are
// literals here rather than tokens because they are SVG coordinates, not CSS:
// tokens.css governs what the page is painted with, and none of these is a
// colour, a type size or a duration.
//
// One width and one horizontal padding for all four SVGs, and that is
// load-bearing. Each is `width: 100%` over the same 1100-unit viewBox, so equal
// padding means the same instant sits in the same screen column in every one of
// them — which is what lets a single crosshair rule cross the whole stack.
const W = 1100;
const H = 300;              // the price lines
const LIQ_H = 96;           // the liquidity strip: short on purpose
const SCATTER_H = 150;      // every individual offer
const AXIS_H = 20;          // the shared x axis, drawn once beneath the stack
const PAD = { l: 64, r: 68, t: 14, b: 24 };

// The strip diverges from a rule in its middle: asks upward, bids downward. So
// its geometry is a centre and an arm rather than a floor and a ceiling.
const LIQ_PAD = 14;
const LIQ_MID = LIQ_PAD + (LIQ_H - 2 * LIQ_PAD) / 2;
const LIQ_ARM = (LIQ_H - 2 * LIQ_PAD) / 2;

// Direct labels are 11px caption text; 13 units of separation keeps two of them
// off each other's baseline with a hair to spare.
const LABEL_GAP = 13;

/** One day, both sides of it. `HistoryPoint` is one row per bucket *per mode*,
 *  which is the right shape for drawing two lines and the wrong one for
 *  answering "what happened on the 14th?" — the question a crosshair asks. */
interface Reading {
  bucket: string;
  /** The day's position on the calendar's own ruler, for gap detection. Null
   *  when the caller bucketed by scan and the label is a uuid. */
  index: number | null;
  /** Where the day sits on the x axis: the first observation of it.
   *
   *  The earliest of the sides present, not their mean. Buy and sell are walked
   *  as separate windows and carry separate `first_seen` stamps, so no single
   *  instant is both — and of the candidates, only `min` is itself a time
   *  somebody's scanner recorded. A mean of two timestamps is a number no
   *  observation ever had. */
  at: number;
  buy?: HistoryPoint;
  sell?: HistoryPoint;
}

/** True for a listing of the plain item, false for one of the several distinct
 *  goods that share its id.
 *
 *  Migration 005_merge_ocr_variant_items.sql files an enchanted or augmented
 *  listing under the *plain* item's id with the difference in three columns, so
 *  that the name still matches the item list. The price consequence is the one
 *  pages/itemQuote.ts states outright: an enchanted or augmented listing is a
 *  different good at a different price. A +6 weapon among the plain weapon's
 *  offers is not an outlier, it is a different market.
 *
 *  The same predicate as `_PLAIN_ITEM` in server/lib/queries.py, which is what
 *  the line chart above is already filtered by. If the two ever disagree, the
 *  two stacked charts stop describing the same good — and `0` counting as plain
 *  is the half most likely to drift: the client writes 0 when the store window
 *  shows no "+N", so a bare `enchant === null` empties the scatter for every
 *  source that reports a zero. */
function isPlain(offer: ItemObservation): boolean {
  return !offer.enchant && offer.element === null && offer.ability === null;
}

export function PriceHistory({
  points,
  offers,
}: {
  points: HistoryPoint[];
  offers: ItemObservation[];
}) {
  // Guarded out here rather than inside Charts so that everything below can
  // assume a non-empty domain. It is not only tidiness: the hooks Charts needs
  // for the crosshair cannot sit after an early return, and `Math.min()` of
  // nothing is Infinity, which scales every coordinate in the stack to NaN.
  if (points.length === 0) return null;
  return <Charts points={points} offers={offers} />;
}

function Charts({
  points,
  offers,
}: {
  points: HistoryPoint[];
  offers: ItemObservation[];
}) {
  const stack = useRef<HTMLDivElement>(null);
  // Which day the crosshair is reading, as an index into `days`. Null is "not
  // reading one", which is the resting state — a chart that always shows a
  // tooltip has picked a day for the reader.
  const [active, setActive] = useState<number | null>(null);

  const chart = useMemo(() => derive(points, offers), [points, offers]);
  const {
    days, buys, sells, plotted, variants, x, y, ticks, scatterY, scatterTicks,
    t0, t1, maxCount, barW, lo, hi, scatterLo, scatterHi,
  } = chart;

  // Nearest day to a horizontal position, in viewBox units. Nearest rather than
  // "the day under the pointer": the days are placed by real time, so they are
  // unevenly spaced and the bands between them have no edges to fall inside.
  const read = useCallback((clientX: number) => {
    const box = stack.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const vx = ((clientX - box.left) / box.width) * W;
    // No horizontal dead zone, deliberately. The first version of this ignored
    // anything outside the plot's own padding, and the last day — which sits
    // exactly on the right edge of the plot, under the direct label that names
    // it — became unreachable: sweeping right to the end of the chart made the
    // tooltip vanish a few pixels before the newest reading, the one the reader
    // most wants. "Which day is this closest to" has an answer everywhere
    // inside the stack, and the pointer is only in the stack because somebody
    // put it there. Leaving is what clears it.
    let best = 0;
    let bestGap = Infinity;
    days.forEach((d, i) => {
      const gap = Math.abs(x(d.at) - vx);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    setActive(best);
  }, [days, x]);

  const step = useCallback((delta: number) => {
    setActive((current) => {
      // From nothing, an arrow key starts at the most recent day rather than
      // the oldest: the reader arrived here to find out what this trades at,
      // and the newest bucket is the answer they came for.
      if (current === null) return delta > 0 ? 0 : days.length - 1;
      return Math.min(days.length - 1, Math.max(0, current + delta));
    });
  }, [days.length]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1 };
    if (event.key in moves) {
      step(moves[event.key]);
    } else if (event.key === "Home") {
      setActive(0);
    } else if (event.key === "End") {
      setActive(days.length - 1);
    } else if (event.key === "Escape") {
      // Dismissible without leaving the chart — the reader can put the tooltip
      // away and keep their place, which is what Escape means everywhere else.
      setActive(null);
    } else {
      return;
    }
    event.preventDefault();
  };

  const reading = active === null ? null : days[active] ?? null;
  const hx = reading === null ? 0 : x(reading.at);
  const tip = reading === null ? null : {
    bid: reading.buy ?? null,
    ask: reading.sell ?? null,
    spread: reading.buy && reading.sell
      ? spreadOf(reading.buy.best, reading.sell.best)
      : null,
  };
  // Which half of the plot the day's marks are in, so the tooltip drops to the
  // other one instead of sitting on top of the values it is describing.
  const marks = reading === null
    ? []
    : [reading.buy?.best, reading.sell?.best]
      .filter((v): v is number => v !== undefined)
      .map(y);
  const highInPlot = marks.length > 0
    && Math.min(...marks) - PAD.t < (H - PAD.b - PAD.t) * 0.45;

  const bucketCount = days.length;
  const line = (rows: HistoryPoint[]): [number, number][] =>
    rows.map((p) => [x(p.first_seen), y(p.best)]);

  // No ±5% area band. The obvious one — best * (1 ± 0.05) — was fabricated: a
  // HistoryPoint carries no min or max for its day, so that envelope asserted a
  // dispersion nobody measured. It also overflowed: niceTicks brackets the raw
  // prices, so on a narrow range the envelope's edges fall outside the viewBox
  // and flood the plot with a filled path (hand-checked: lo=2000, hi=2010 puts
  // the lower edge at y=2896 on a 300-tall canvas).
  //
  // The spread band below is not a return of it, and the difference is the
  // whole rule: both of ITS edges are observations. The top is an ask somebody
  // typed into a store window and the bottom is a bid somebody else typed into
  // theirs. It asserts nothing the two lines do not already assert — it only
  // makes the distance between them readable, which is the question this page
  // exists to answer.
  const paired = days.filter((d) => d.buy !== undefined && d.sell !== undefined);
  const spreadRuns = segments(paired, (d) => d.index);

  const lastBuy = buys[buys.length - 1];
  const lastSell = sells[sells.length - 1];
  // DESIGN.md: direct-label the first and last value of each series, nothing
  // else. Skipped for a single-point series, where first and last are one mark.
  const firstBuy = buys.length > 1 ? buys[0] : undefined;
  const firstSell = sells.length > 1 ? sells[0] : undefined;
  // The right-hand pair sits outside the plot, so nothing but the other label
  // can collide with it — and on a market whose bid and ask have converged,
  // which is the interesting case rather than the exceptional one, they land on
  // the same baseline. The left-hand pair is handled differently: it sits on
  // top of the plot, where the thing it collides with is its own 2px line, and
  // a fixed offset cannot help because the line leaves the point in whatever
  // direction the next day took it. That one gets a ground-coloured halo
  // instead — DESIGN.md's own answer for marks that overlap — plus enough
  // vertical clearance that the halo is not doing all the work.
  const [lastSellY, lastBuyY] = lastSell && lastBuy
    ? separate(y(lastSell.best), y(lastBuy.best), LABEL_GAP)
    : [lastSell ? y(lastSell.best) : 0, lastBuy ? y(lastBuy.best) : 0];

  const spanLabel = `${day(t0)} to ${day(t1)}`;

  return (
    <>
      <div className="legend">
        {buys.length > 0 ? (
          <span><i className="key buy" /> WTB — what traders pay</span>
        ) : null}
        {sells.length > 0 ? (
          <span><i className="key sell" /> WTS — what traders ask</span>
        ) : null}
        {ticks.length > 1 ? (
          <span className="axis-note">Price axis starts at {adena(ticks[0])}, not zero.</span>
        ) : null}
      </div>

      {/* Plain listings only, said rather than done quietly. These offers are
          not missing and they are not outliers to be trimmed — they are other
          goods, and the tables below print every one of them with its pill.
          Without the sentence a reader who saw a +6 in the table and not on the
          chart would reasonably conclude the chart was broken. */}
      {variants > 0 ? (
        <p className="legend note">
          <span>
            Plain listings only — {plural(variants, "offer")} here
            {" "}
            {variants === 1 ? "is" : "are"} enchanted, elemented or augmented, which is a
            different good at a different price. They are in the tables below, each with
            its pill.
          </span>
        </p>
      ) : null}

      {/* One interaction surface for the whole stack, because one crosshair
          crosses all of it. Focusable and driven by arrow keys as well as the
          pointer: DESIGN.md makes a crosshair the default rather than an
          enhancement, and a chart only a mouse can interrogate is a screenshot
          for half its readers. */}
      <div
        className="chart-stack"
        ref={stack}
        tabIndex={0}
        role="group"
        aria-label={
          `Price history, ${plural(bucketCount, "day")}, ${spanLabel}. ` +
          "Use the left and right arrow keys to read one day at a time."
        }
        onPointerMove={(e) => read(e.clientX)}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        <div className="plot">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={
              `Best bid and best ask per day over ${plural(bucketCount, "day")}, ` +
              `${adena(lo)} to ${adena(hi)} adena.`
            }
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line x1={PAD.l} y1={y(tick)} x2={W - PAD.r} y2={y(tick)} className="grid" />
                <text x={PAD.l - 8} y={y(tick) + 4} className="axis" textAnchor="end">
                  {adena(tick)}
                </text>
              </g>
            ))}

            {/* Under the lines, over the grid: it is the region the two lines
                bound, so anything that draws it on top of them is drawing a
                wash over the measurement. Broken wherever the pair of days is
                not consecutive, for the same reason the lines are. */}
            {spreadRuns.map((run) => (
              <path
                key={`spread-${run[0].bucket}`}
                d={band(
                  // Screen-top edge and screen-bottom edge, taken per day,
                  // rather than "the buy edge and the sell edge". On a crossed
                  // market the two lines swap sides, and a polygon built from
                  // the series instead of from the geometry ties itself into a
                  // bowtie whose lobes wind opposite ways — half of it then
                  // vanishes under a nonzero fill. Min and max can never cross,
                  // so this outline is always simple. Which side is on top is
                  // not lost: the two coloured lines drawn over it say so.
                  //
                  // Both edges of a day share one x — the day's own `at` —
                  // because buy and sell carry separate first_seen stamps and a
                  // polygon cannot have two x positions per vertex. Within a
                  // scan those stamps are seconds apart, which is a fraction of
                  // a pixel here; the lines themselves still sit on their own
                  // exact times.
                  run.map((d) => [x(d.at), Math.min(y(d.buy!.best), y(d.sell!.best))]),
                  run.map((d) => [x(d.at), Math.max(y(d.buy!.best), y(d.sell!.best))]),
                )}
                className="band spread"
              />
            ))}

            <Series rows={sells} mode="sell" line={line} />
            <Series rows={buys} mode="buy" line={line} />

            {firstSell ? (
              <text
                x={x(firstSell.first_seen) + 6} y={y(firstSell.best) - 9}
                className="label sell ground" textAnchor="start"
              >
                {adena(firstSell.best)}
              </text>
            ) : null}
            {firstBuy ? (
              <text
                x={x(firstBuy.first_seen) + 6} y={y(firstBuy.best) + 17}
                className="label buy ground" textAnchor="start"
              >
                {adena(firstBuy.best)}
              </text>
            ) : null}
            {lastSell ? (
              <text x={W - PAD.r + 8} y={lastSellY + 4} className="label sell">
                {adena(lastSell.best)}
              </text>
            ) : null}
            {lastBuy ? (
              <text x={W - PAD.r + 8} y={lastBuyY + 4} className="label buy">
                {adena(lastBuy.best)}
              </text>
            ) : null}

            {reading ? (
              <g className="cross">
                <line x1={hx} y1={PAD.t} x2={hx} y2={H - PAD.b} className="crosshair" />
                {reading.sell ? (
                  <circle cx={x(reading.sell.first_seen)} cy={y(reading.sell.best)} r={4}
                    className="pin sell" />
                ) : null}
                {reading.buy ? (
                  <circle cx={x(reading.buy.first_seen)} cy={y(reading.buy.best)} r={4}
                    className="pin buy" />
                ) : null}
              </g>
            ) : null}
          </svg>

          {/* aria-hidden, with the same reading spoken by the live region at the
              bottom of the stack instead. A tooltip that is mounted and
              unmounted is an unreliable live region — several screen readers
              announce only changes to a region that was already in the tree —
              and marking this up as one as well would double-announce wherever
              it does work. */}
          {tip && reading ? (
            <div
              className={`chart-tip ${highInPlot ? "low" : "high"}`}
              style={{ left: `${(hx / W) * 100}%` }}
              data-anchor={hx < W * 0.24 ? "start" : hx > W * 0.76 ? "end" : "middle"}
              aria-hidden="true"
            >
              <p className="tip-day">{day(reading.at)}</p>
              <dl>
                <div>
                  <dt><i className="key sell" /> WTS</dt>
                  <dd className="num">{adena(tip.ask?.best ?? null)}</dd>
                  <dd className="depth">
                    {tip.ask
                      ? `${plural(tip.ask.offers, "offer")} · ${plural(tip.ask.traders, "trader")}`
                      : "not offered"}
                  </dd>
                </div>
                <div>
                  <dt><i className="key buy" /> WTB</dt>
                  <dd className="num">{adena(tip.bid?.best ?? null)}</dd>
                  <dd className="depth">
                    {tip.bid
                      ? `${plural(tip.bid.offers, "offer")} · ${plural(tip.bid.traders, "trader")}`
                      : "not wanted"}
                  </dd>
                </div>
              </dl>
              {/* Absent, not zero, when only one side was seen. A spread needs
                  two observations and there is no honest figure for one. */}
              <p className="tip-spread">
                {tip.spread === null
                  ? "No spread — only one side of the market that day."
                  : `Spread ${signed(tip.spread.gap)}${
                    tip.spread.pct === null ? "" : ` (${signed(Math.round(tip.spread.pct))}%)`}`}
              </p>
            </div>
          ) : null}
        </div>

        {/* What stood behind each day's price. HistoryPoint has carried these
            two counts all along and nothing drew them, so a price held up by
            one lone listing looked exactly like a price nine traders agreed on.
            Asks up and bids down from a shared zero rule rather than side by
            side: at this height two dodged bars per day are 4px slivers, and
            the mirror reads as depth at a glance without asking for any more
            room than the strip already has. */}
        <p className="legend">
          <span><i className="key sell" /> asks above</span>
          <span><i className="key buy" /> bids below</span>
          <span className="axis-note">
            Offers behind each day&rsquo;s price. Count axis, so it starts at zero.
          </span>
        </p>
        <svg
          viewBox={`0 0 ${W} ${LIQ_H}`}
          role="img"
          aria-label={
            `Offers behind each day's price, zero to ${maxCount} per side, ` +
            "asks above the rule and bids below."
          }
        >
          <line x1={PAD.l} y1={LIQ_MID} x2={W - PAD.r} y2={LIQ_MID} className="grid" />
          <text x={PAD.l - 8} y={LIQ_MID - LIQ_ARM + 4} className="axis" textAnchor="end">
            {maxCount}
          </text>
          <text x={PAD.l - 8} y={LIQ_MID + 4} className="axis" textAnchor="end">0</text>
          <text x={PAD.l - 8} y={LIQ_MID + LIQ_ARM + 4} className="axis" textAnchor="end">
            {maxCount}
          </text>

          {days.map((d, i) => {
            const cx = x(d.at);
            const up = d.sell ? (d.sell.offers / maxCount) * LIQ_ARM : 0;
            const down = d.buy ? (d.buy.offers / maxCount) * LIQ_ARM : 0;
            const on = i === active ? " on" : "";
            return (
              <g key={d.bucket}>
                {d.sell ? (
                  <rect x={cx - barW / 2} y={LIQ_MID - up} width={barW} height={up}
                    className={`bar sell${on}`} />
                ) : null}
                {d.buy ? (
                  <rect x={cx - barW / 2} y={LIQ_MID} width={barW} height={down}
                    className={`bar buy${on}`} />
                ) : null}
              </g>
            );
          })}
          {reading ? (
            <line x1={hx} y1={LIQ_PAD} x2={hx} y2={LIQ_H - LIQ_PAD} className="crosshair" />
          ) : null}
        </svg>

        {plotted.length > 0 ? (
          <>
            <p className="legend">
              <span>
                {offers.length >= 1000
                  ? "The most recent 1,000 offers only — a gap on the left is this view's limit, not an absence of trade."
                  : "Every plain offer on its own, so density reads as darkness."}
              </span>
              {/* Its own price scale, and that has to be said out loud.
                  Sharing the chart above's domain is what this stack used to
                  do, and one wide offer then set the y-axis of BOTH — squashing
                  the two price lines into a ribbon with empty bands above and
                  below them. Splitting the domains fixes the line chart and
                  introduces exactly one new way to be misread, which is a
                  reader assuming the two charts still share a scale. This
                  sentence closes it.

                  The numbers are the TICK range, not the offers' own min and
                  max. What a reader can check is what is printed on the axis a
                  few pixels below, and quoting a different pair beside it —
                  840,447 where the axis says 500,000 — reads as a
                  contradiction rather than as extra precision. */}
              <span className="axis-note">
                Its own price scale, {adena(scatterTicks[0])} to
                {" "}{adena(scatterTicks[scatterTicks.length - 1])}, not zero.
              </span>
            </p>
            <svg
              viewBox={`0 0 ${W} ${SCATTER_H}`}
              role="img"
              aria-label={
                `${plural(plotted.length, "individual plain offer")} over the same ` +
                `${plural(bucketCount, "day")}, priced from ${adena(scatterLo)} ` +
                `to ${adena(scatterHi)} adena.`
              }
            >
              <line x1={PAD.l} y1={SCATTER_H - PAD.b} x2={W - PAD.r} y2={SCATTER_H - PAD.b}
                className="grid" />
              {/* DESIGN.md: a truncated price axis is labelled, never implied.
                  There are two such axes in this stack now, and this is the
                  second one's label. */}
              <text x={PAD.l - 8} y={scatterY(scatterTicks[0]) + 4} className="axis"
                textAnchor="end">
                {adena(scatterTicks[0])}
              </text>
              <text x={PAD.l - 8} y={scatterY(scatterTicks[scatterTicks.length - 1]) + 4}
                className="axis" textAnchor="end">
                {adena(scatterTicks[scatterTicks.length - 1])}
              </text>
              {plotted.map((offer) => (
                <circle key={offer.id} cx={x(offer.observed_at)} cy={scatterY(offer.price)} r={4}
                  className={`dot ${offer.mode}`} />
              ))}
              {reading ? (
                <line x1={hx} y1={PAD.t} x2={hx} y2={SCATTER_H - PAD.b} className="crosshair" />
              ) : null}
            </svg>
          </>
        ) : null}

        {/* The same reading as the tooltip, as a sentence, in a region that is
            always in the tree so that changing it is an announcement. Empty
            between readings rather than removed, for the same reason.

            It is a sentence and not the tooltip's own layout because a grid of
            two labels, two figures and two depth lines is read out as six
            disconnected fragments; "Sep 3. Ask 1,638,452 from 5 traders…" is
            the same information in the order somebody would say it. */}
        <p className="sr-live" role="status" aria-live="polite">
          {reading === null ? "" : spoken(reading)}
        </p>

        {/* The x axis, drawn once under everything rather than once per chart.
            All four SVGs share this scale exactly, so repeating the dates three
            times would be three copies of one fact — and putting them under the
            top chart alone would label the middle of a stack. */}
        <svg viewBox={`0 0 ${W} ${AXIS_H}`} aria-hidden="true">
          <text x={PAD.l} y={12} className="axis" textAnchor="start">{day(t0)}</text>
          <text x={W - PAD.r} y={12} className="axis" textAnchor="end">{day(t1)}</text>
        </svg>
      </div>
    </>
  );
}

/** What the crosshair is reading, said aloud.
 *
 *  Kept next to the tooltip's markup so the two cannot drift: if a figure is
 *  worth showing a reader it is worth telling one, and the failure mode of a
 *  separate summary is that it quietly goes stale. */
function spoken(reading: Reading): string {
  const parts = [day(reading.at)];
  for (const [label, point] of [
    ["Ask", reading.sell],
    ["Bid", reading.buy],
  ] as const) {
    parts.push(point === undefined
      ? `no ${label.toLowerCase()}`
      : `${label} ${adena(point.best)} from ${plural(point.traders, "trader")}`
        + ` across ${plural(point.offers, "offer")}`);
  }
  if (reading.buy && reading.sell) {
    const gap = spreadOf(reading.buy.best, reading.sell.best);
    parts.push(`spread ${signed(gap.gap)}`
      + (gap.pct === null ? "" : `, ${signed(Math.round(gap.pct))} per cent`));
  }
  return `${parts.join(". ")}.`;
}

/** One price series, broken wherever the days are.
 *
 *  A single polyline through every bucket draws a run of unscanned days as one
 *  long straight segment, which asserts a price for every day along it. That is
 *  the same fabrication the ±5% band was deleted for; a line is only a quieter
 *  place to do it. So the run is cut, and a dashed half-opacity bridge spans the
 *  hole — enough for the eye to keep the series together, visibly not a
 *  measurement. */
function Series({
  rows,
  mode,
  line,
}: {
  rows: HistoryPoint[];
  mode: "buy" | "sell";
  line: (rows: HistoryPoint[]) => [number, number][];
}) {
  if (rows.length === 0) return null;
  const runs = segments(rows, (p) => dayIndex(p.bucket));
  return (
    <>
      {runs.slice(1).map((run, i) => (
        <path
          key={`bridge-${run[0].bucket}`}
          d={path(line([runs[i][runs[i].length - 1], run[0]]))}
          className={`line ${mode} bridge`}
        />
      ))}
      {runs.map((run) => (
        <path key={run[0].bucket} d={path(line(run))} className={`line ${mode}`} />
      ))}
    </>
  );
}

/** Everything the charts are drawn from, derived once.
 *
 *  Pulled out of the component body so the crosshair's state changes — which
 *  fire on every pointer move — do not re-derive a few hundred offers and
 *  re-run niceTicks four times a frame. */
function derive(points: HistoryPoint[], offers: ItemObservation[]) {
  const buys = points.filter((p) => p.mode === "buy");
  const sells = points.filter((p) => p.mode === "sell");

  // Both charts share ONE x axis, and it is real time in epoch seconds.
  //
  // The tempting alternative — index the buckets and place each offer by
  // formatting its observed_at into the same date string — cannot work. The
  // server buckets days in APP_TIMEZONE and renders them as YYYY-MM-DD; a
  // browser formats in the viewer's own zone and locale. The two disagree on
  // format always and on which day sometimes, and the failure is silent: an
  // empty scatter, not an error. Each HistoryPoint already carries first_seen,
  // so positioning by time needs no join at all — and it draws a week with no
  // scans as the gap it really was rather than evenly spacing it away.
  //
  // (scale.ts's dayIndex does read the bucket label, and that is a different
  // operation: both sides of its comparisons are server-issued labels, so the
  // viewer's zone never enters the arithmetic. Its docstring says so.)
  //
  // Only priced observations can be plotted. A package row's price is null, and
  // null coerces to 0 in Math.min, which would silently drag the whole axis
  // down to zero and put the point on the floor rather than leave it out.
  //
  // Bids and asks only, matching the line chart above and the server's own
  // `o.mode IN ('buy', 'sell')`. A craft fee is what a crafter charges to make
  // the thing, not what the thing costs — drawn among the asks it reads as a
  // giveaway, and it has no `.dot` colour of its own, so until now it rendered
  // as an unstyled black circle. And plain listings only, for the reason
  // `isPlain` gives.
  const plotted = offers.filter(
    (o): o is ItemObservation & { price: number } =>
      (o.mode === "buy" || o.mode === "sell") && o.price !== null && isPlain(o),
  );
  const variants = offers.filter(
    (o) => (o.mode === "buy" || o.mode === "sell") && o.price !== null && !isPlain(o),
  ).length;

  const times = [...points.map((p) => p.first_seen), ...plotted.map((o) => o.observed_at)];
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);

  // Two price domains, not one. Built from one list, the widest scatter point
  // set the y-axis of the line chart as well, and a single outlying offer
  // squashed both price lines into a ribbon with large empty bands above and
  // below it. The x scale stays shared and exact — that is what makes the stack
  // one chart — but a y scale is per-chart, and DESIGN.md's answer for two
  // series that cannot share a scale is precisely two charts sharing an x axis.
  const bests = points.map((p) => p.best);
  const lo = Math.min(...bests);
  const hi = Math.max(...bests);
  const ticks = niceTicks(lo, hi);

  const scatterPrices = plotted.map((o) => o.price);
  const scatterLo = scatterPrices.length > 0 ? Math.min(...scatterPrices) : lo;
  const scatterHi = scatterPrices.length > 0 ? Math.max(...scatterPrices) : hi;
  const scatterTicks = niceTicks(scatterLo, scatterHi);

  const x = linear([t0, t1], [PAD.l, W - PAD.r]);
  const y = linear([ticks[0], ticks[ticks.length - 1]], [H - PAD.b, PAD.t]);
  const scatterY = linear(
    [scatterTicks[0], scatterTicks[scatterTicks.length - 1]],
    [SCATTER_H - PAD.b, PAD.t],
  );

  // One row per day with both sides on it. The API returns one row per bucket
  // per mode, which draws two lines beautifully and cannot answer the only
  // question a crosshair asks.
  const byBucket = new Map<string, Reading>();
  for (const p of points) {
    const found = byBucket.get(p.bucket);
    const reading = found ?? {
      bucket: p.bucket, index: dayIndex(p.bucket), at: p.first_seen,
    };
    reading[p.mode] = p;
    reading.at = Math.min(reading.at, p.first_seen);
    if (!found) byBucket.set(p.bucket, reading);
  }
  const days = [...byBucket.values()].sort((a, b) => a.at - b.at);

  // The count axis is shared between the two halves of the strip so that a tall
  // ask bar and a tall bid bar mean the same number. Floored at 1 so a strip
  // with no offers at all divides by something.
  const maxCount = Math.max(
    1,
    ...points.map((p) => p.offers),
  );

  // A bar is a fraction of its calendar slot, not of the plot divided by the
  // number of days observed. Measured the second way, a month with a fortnight
  // missing draws bars twice as wide as its neighbours' spacing and they
  // overlap into a solid block.
  const indices = days.map((d) => d.index).filter((i): i is number => i !== null);
  const slots = indices.length > 1
    ? Math.max(...indices) - Math.min(...indices) + 1
    : Math.max(1, days.length);
  const barW = Math.max(2, Math.min(12, ((W - PAD.l - PAD.r) / slots) * 0.62));

  return {
    days, buys, sells, plotted, variants, x, y, ticks, scatterY, scatterTicks,
    t0, t1, maxCount, barW, lo, hi, scatterLo, scatterHi,
  };
}
